import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { RefreshCw, TrendingUp, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../services/logger.service';
import { Deal, DealStage } from '../types/deal';
import { dealAPI } from '../lib/deal-api';

interface UpdateDealStatusModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deal: Deal | null;
  onDealUpdated: (dealId: string, updates: Partial<Deal>) => void;
}

// Maps frontend DealStage values to the backend `status` field values
const STAGE_TO_STATUS: Record<DealStage, string> = {
  lead: 'pending',
  'viewing-scheduled': 'viewing-scheduled',
  'offer-made': 'offer-made',
  'offer-accepted': 'offer-accepted',
  financing: 'financing',
  inspection: 'inspection',
  closing: 'closing',
  closed: 'closed',
  lost: 'cancelled',
};

const stageConfig: Record<DealStage, { label: string; color: string; probability: number }> = {
  'lead': { label: 'Lead', color: 'bg-gray-100 text-gray-800', probability: 20 },
  'viewing-scheduled': { label: 'Viewing Scheduled', color: 'bg-blue-100 text-blue-800', probability: 30 },
  'offer-made': { label: 'Offer Made', color: 'bg-yellow-100 text-yellow-800', probability: 50 },
  'offer-accepted': { label: 'Offer Accepted', color: 'bg-green-100 text-green-800', probability: 70 },
  'financing': { label: 'Financing', color: 'bg-purple-100 text-purple-800', probability: 75 },
  'inspection': { label: 'Inspection', color: 'bg-indigo-100 text-indigo-800', probability: 80 },
  'closing': { label: 'Closing', color: 'bg-orange-100 text-orange-800', probability: 90 },
  'closed': { label: 'Closed (Sold)', color: 'bg-emerald-100 text-emerald-800', probability: 100 },
  'lost': { label: 'Lost', color: 'bg-red-100 text-red-800', probability: 0 },
};

export function UpdateDealStatusModal({ open, onOpenChange, deal, onDealUpdated }: UpdateDealStatusModalProps) {
  const [newStage, setNewStage] = useState<DealStage>(deal?.stage || 'lead');
  const [finalAmount, setFinalAmount] = useState<string>(deal?.finalAmount?.toString() || deal?.offerAmount?.toString() || '');
  const [commissionRate, setCommissionRate] = useState<string>('');
  const [updateNotes, setUpdateNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update state when deal changes
  useEffect(() => {
    if (deal) {
      setNewStage(deal.stage);
      setFinalAmount(deal.finalAmount?.toString() || deal.offerAmount?.toString() || '');
      setCommissionRate('');
      setUpdateNotes('');
    }
  }, [deal]);

  const handleSubmit = async () => {
    if (!deal) return;

    setIsSubmitting(true);

    try {
      // Backend accepts: status, salePrice, commission, agentId, buyerName, sellerName, propertyAddress, listingId
      const backendPayload: Record<string, any> = {
        status: STAGE_TO_STATUS[newStage],
      };

      if (newStage === 'closed' && finalAmount) {
        backendPayload.salePrice = Number(finalAmount);
        if (commissionRate) {
          backendPayload.commissionRate = Number(commissionRate);
        }
      }

      if (updateNotes) {
        backendPayload.notes = deal.notes
          ? `${deal.notes}\n\n[${new Date().toLocaleDateString()}] ${updateNotes}`
          : updateNotes;
      }

      await dealAPI.updateDeal(deal.id, backendPayload as any);

      // Update local state with frontend-friendly fields
      const localUpdates: Partial<Deal> = {
        stage: newStage,
        probability: stageConfig[newStage].probability,
      };
      if (newStage === 'closed' && finalAmount) {
        localUpdates.finalAmount = Number(finalAmount);
        const rate = commissionRate ? Number(commissionRate) : 0;
        if (rate > 0) {
          localUpdates.commission = Math.round(Number(finalAmount) * rate / 100);
        }
      }
      if (updateNotes) {
        localUpdates.notes = backendPayload.notes;
      }
      onDealUpdated(deal.id, localUpdates);
      toast.success(`Deal status updated to ${stageConfig[newStage].label}!`);
      
      // Reset form
      setUpdateNotes('');
      setCommissionRate('');
      onOpenChange(false);
    } catch (error) {
      logger.error('Error updating deal', error as Error, undefined, 'UpdateDealStatusModal');
      toast.error('Failed to update deal. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Update Deal Status</DialogTitle>
          <DialogDescription>
            Change the stage and update deal details
          </DialogDescription>
        </DialogHeader>

        {deal && (
          <div className="space-y-6">
            {/* Current Deal Info */}
            <div className="p-4 bg-muted/50 rounded-lg border">
              <h4 className="font-semibold mb-1">{deal.propertyTitle}</h4>
              <p className="text-sm text-muted-foreground mb-2">{deal.propertyAddress}</p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Client: {deal.clientName}</span>
                <span className="font-semibold">R {(deal.finalAmount || deal.offerAmount || 0).toLocaleString()}</span>
              </div>
            </div>

            {/* Update Stage */}
            <div className="space-y-2">
              <Label htmlFor="newStage">
                New Stage <span className="text-destructive">*</span>
              </Label>
              <Select
                value={newStage}
                onValueChange={(value: DealStage) => setNewStage(value)}
              >
                <SelectTrigger id="newStage">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lead">Lead (20% probability)</SelectItem>
                  <SelectItem value="viewing-scheduled">Viewing Scheduled (30%)</SelectItem>
                  <SelectItem value="offer-made">Offer Made (50%)</SelectItem>
                  <SelectItem value="offer-accepted">Offer Accepted (70%)</SelectItem>
                  <SelectItem value="financing">Financing (75%)</SelectItem>
                  <SelectItem value="inspection">Inspection (80%)</SelectItem>
                  <SelectItem value="closing">Closing (90%)</SelectItem>
                  <SelectItem value="closed">Closed - Sold (100%)</SelectItem>
                  <SelectItem value="lost">Lost (0%)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Current: {stageConfig[deal.stage].label} ({deal.probability}% probability)
              </p>
            </div>

            {/* Final Amount for Closed Deals */}
            {newStage === 'closed' && (
              <div className="space-y-3 p-4 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="space-y-2">
                  <Label htmlFor="finalAmount" className="text-green-900 dark:text-green-100">
                    Final Sale Amount <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="finalAmount"
                    type="number"
                    value={finalAmount}
                    onChange={(e) => setFinalAmount(e.target.value)}
                    placeholder="R 0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="commissionRate" className="text-green-900 dark:text-green-100">
                    Commission % (optional)
                  </Label>
                  <Input
                    id="commissionRate"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={commissionRate}
                    onChange={(e) => setCommissionRate(e.target.value)}
                    placeholder="e.g. 7.5"
                  />
                  {commissionRate && finalAmount && (
                    <p className="text-xs text-green-700 dark:text-green-300">
                      Commission: R {Math.round(Number(finalAmount) * Number(commissionRate) / 100).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Update Notes */}
            <div className="space-y-2">
              <Label htmlFor="updateNotes">Update Notes</Label>
              <Textarea
                id="updateNotes"
                value={updateNotes}
                onChange={(e) => setUpdateNotes(e.target.value)}
                placeholder="Add notes about this status update..."
                rows={3}
              />
            </div>

            {/* Probability Indicator */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Success Probability</span>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="font-semibold">{stageConfig[newStage].probability}%</span>
                </div>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-green-600 h-full rounded-full transition-all duration-300"
                  style={{ width: `${stageConfig[newStage].probability}%` }}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                data-testid="update-deal-cancel-button"
                variant="outline"
                className="flex-1"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                data-testid="update-deal-status-button"
                className="flex-1"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Update Status
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}